/**
 * Common API and event types - replaces `any` with proper definitions
 */

/** API error shape from backend or RTK Query */
export interface ApiError {
  status?: number;
  data?: {
    message?: string;
    [key: string]: unknown;
  };
  message?: string;
}

/** Type guard for API errors */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    ("data" in error || "message" in error)
  );
}

/** Extract error message from unknown error */
export function getErrorMessage(error: unknown, fallback = "An error occurred"): string {
  if (isApiError(error)) {
    return error?.data?.message ?? error?.message ?? fallback;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

/** MUI Table pagination event */
export type PaginationChangeEvent = React.MouseEvent<HTMLButtonElement> | null;
export type PageChangeHandler = (
  event: PaginationChangeEvent,
  newPage: number
) => void;

/** MUI Select/Input change event */
export type SelectChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
export type AutocompleteChangeEvent = React.SyntheticEvent;

/** Generic API response wrapper */
export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

/** Skills/Industries list item for table display */
export interface SkillTableRow {
  _id: string;
  id?: string;
  name?: string;
  name_ar?: string;
  status?: boolean;
  createdAt?: string;
}

export interface IndustryTableRow extends SkillTableRow {}

/** Bank list item for table display */
export interface BankTableRow extends SkillTableRow {}

/** Promotional banner list item for table display */
export interface PromotionalBannerTableRow {
  _id: string;
  id?: string;
  category?: string | { _id: string; name?: string };
  texts?: Record<string, string>;
  status?: boolean;
  isPrimary?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

/** Work history item from API */
export interface WorkHistoryItem {
  _id: string;
  employerId?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  category?: { name?: string };
  subCategory?: { name?: string };
  serviceNames?: string[];
  subTotal?: number | string;
  total?: number | string;
  createdAt?: string;
  skills?: (string | number)[];
  startTime?: string;
  endTime?: string;
  notes?: string;
  media?: string[];
  [key: string]: unknown;
}
